// SCHEMA_VERSION = 1
//
// Compact state delta contract shared between API and frontend.
// All deltas are readonly wire-format snapshots — never mutated after publish.
// The CHANNEL is the dispatcher; the `type` field discriminates within channels
// that carry multiple variants (only show-state today).
//
// ⚠️ CROSS-APP CHANNEL INVARIANT — READ BEFORE RENAMING ANY CHANNEL
// ─────────────────────────────────────────────────────────────────
// Ably channels are subscribed to from MORE places than `apps/api` and
// `apps/frontend`. When you rename, consolidate, or fold a channel:
//
//   1. apps/api          — publishers (resolvers, hot-state writer, services)
//   2. apps/frontend     — subscribers (stores, composables, services)
//   3. apps/simulfest    — subscribers (Node/Puppeteer container). Does NOT
//                          import @f3/shared (CommonJS); channel names are
//                          HARDCODED STRINGS in apps/simulfest/src/sockets.ts.
//                          Must be updated by hand.
//   4. apps/embed, apps/feature-flags-admin, demos/* — grep before assuming
//                          they don't subscribe.
//
// 2026-05-13 incident: plan-060 TICKET-013 consolidated `show-state` →
// `show-state-v2`. apps/simulfest was missed — production shows could not
// transition to live. If you mark a channel `Zombie (TICKET-XXX)` below,
// search the whole monorepo for the OLD channel-name STRING (not just the
// REALTIME_CHANNELS key) and confirm every consumer has moved.
//
// When folding channels into a discriminated union (like show-state-v2):
// every consumer MUST filter on `delta.type` before reading fields — different
// variants may have overlapping field names with overlapping string values
// from different enums (e.g. `status: "live"` means different things in
// ShowStateSnapshotDelta vs SimulcastInstanceStateDelta).
//
// Field origin annotations:
//   (persisted column X) — value read from a Postgres column
//   (runtime) — server-authored current-state runtime value, not persisted in Postgres
//   (legacy client-published) — non-authoritative state retained until its owning
//                               surface moves to backend-authored runtime streams

// ---------------------------------------------------------------------------
// Enum mirrors — defined here as string literal unions to avoid pulling the
// Prisma client into @f3/shared. Values must stay in sync with the Prisma
// schema enums (ShowStatus, ShowSimulcastInstanceStatus, ContestStatus).
// ---------------------------------------------------------------------------

/** Mirror of Prisma ShowStatus enum */
export type ShowStatus =
  | "draft"
  | "scheduled"
  | "live"
  | "ended"
  | "deleted"
  | "video_cast";

/** Mirror of Prisma ShowSimulcastInstanceStatus enum */
export type ShowSimulcastInstanceStatus =
  | "pending"
  | "booted_up"
  | "live"
  | "ended"
  | "failed";

/** Mirror of Prisma ContestStatus enum */
export type ContestStatus = "draft" | "ready" | "ended" | "deleted";

export type QuizTransition =
  | "TO_QUESTION"
  | "TO_CORRECT_ANSWER"
  | "TO_POLL_ANSWER"
  | "TO_LEADERBOARD"
  | "TO_WINNER";

export type RaffleRuntimeStatus =
  | "idle"
  | "ready"
  | "running"
  | "ended"
  | "cleared";

export type RaffleStep =
  | "RAFFLE"
  | "WINNER"
  | "REWARD"
  | "REWARD_GENERIC"
  | null;

export type RaffleSlotState = "IDLE" | "ACTIVE" | "DONE";

export type LayoutContentPosition = "FIRST" | "LAST";

// ---------------------------------------------------------------------------
// Base envelope
// ---------------------------------------------------------------------------

/**
 * Base envelope carried by every delta.
 * Frontend uses streamId to scope its lastApplied epoch map. entityId remains
 * the business object id, not the ordering key.
 *
 * Event streams must not use RealtimeStateDelta unless they are
 * server-authored current-state streams. Client-publishable interaction events
 * need separate event payload contracts.
 */
export interface RealtimeStateDelta {
  /** Literal string discriminant — uniquely identifies the delta shape */
  readonly type: string;
  /** Stable current-state stream id, e.g. show:{showId}:primary-stage */
  readonly streamId: string;
  /** Monotonic per streamId — produced by EpochService.nextStream */
  readonly epoch: number;
  /** e.g. show id, quiz id, raffle id */
  readonly entityId: string;
  /** Server commit id for correlating DDB, Ably, mutation responses, snapshots */
  readonly commitId: string;
  /** Host command id that caused the commit, or null for server/system writes */
  readonly commandId: string | null;
}

// ---------------------------------------------------------------------------
// Runtime stream registry
// ---------------------------------------------------------------------------

export const STREAMS = {
  primaryStage: (showId: string) => `show:${showId}:primary-stage`,
  showState: (showId: string) => `show:${showId}:state`,
  layout: (showId: string) => `show:${showId}:layout`,
  agenda: (showId: string) => `show:${showId}:agenda`,
  media: (showId: string) => `show:${showId}:media`,
  offer: (showId: string) => `show:${showId}:offer`,
  simulcast: (instanceId: string) => `simulcast:${instanceId}:state`,
  quizState: (quizId: string) => `quiz:${quizId}:state`,
  quizTransition: (quizId: string) => `quiz:${quizId}:transition`,
  quizWinner: (quizId: string) => `quiz:${quizId}:winner`,
  raffleState: (raffleId: string) => `raffle:${raffleId}:state`,
  raffleWinner: (raffleId: string) => `raffle:${raffleId}:winner`,
  raffleSlot: (raffleId: string) => `raffle:${raffleId}:slot`,
  raffleStep: (raffleId: string) => `raffle:${raffleId}:step`,
  /**
   * Presentation-plan runtime stream. One stream per stage slot —
   * the stream id carries the slot so independently-versioned slots
   * (content, agenda, layout) never share an epoch counter.
   */
  stagePlan: (showId: string, slotId: StageSlotId) =>
    `show:${showId}:stage-plan:${slotId}`,
} as const;

// ---------------------------------------------------------------------------
// Primary stage runtime surface: show:{showId}:primary-stage
// ---------------------------------------------------------------------------

export type PrimaryStageSurface =
  | { readonly kind: "empty" }
  | {
      readonly kind: "quiz";
      readonly quizId: string;
      readonly questionId: string | null;
    }
  | {
      readonly kind: "raffle";
      readonly raffleId: string;
      readonly phase: RaffleRuntimeStatus;
    }
  | { readonly kind: "media"; readonly mediaUrl: string }
  | { readonly kind: "offer"; readonly offer: ShowOfferPayload };

export interface PrimaryStageStateDelta extends RealtimeStateDelta {
  readonly type: "primary-stage-state";
  readonly showId: string;
  readonly surface: PrimaryStageSurface;
}

// ---------------------------------------------------------------------------
// Show-state channel: fanfest:{showId}:show-state
// ---------------------------------------------------------------------------

/**
 * Published by the API on `fanfest:{showId}:show-state`.
 * Carries persisted fields only — no currentSpeakerUserId / canFansJoinStage /
 * hasSetupSimulfestPreparation / currentQuizId / currentRaffleId / currentAgendaItemId.
 */
export interface ShowStateSnapshotDelta extends RealtimeStateDelta {
  readonly type: "state";
  /** (persisted column shows.status) */
  readonly status: ShowStatus;
}

/**
 * Recording-ready notification, piggy-backed on the show-state channel.
 * Discriminated by type === "recording-ready" so subscribers can branch without
 * a separate channel subscription. This is the existing in-codebase precedent
 * (see show-realtime.service.ts:sendRecordingReady).
 */
export interface ShowRecordingReadyDelta extends RealtimeStateDelta {
  readonly type: "recording-ready";
  /** (event payload — not persisted) */
  readonly variant: "landscape" | "portrait";
  /** (event timestamp — not persisted) */
  readonly completedAt: number;
}

/**
 * Mux-rotated notification, piggy-backed on the show-state channel.
 * Carries no field payload — its sole purpose is to bump the show entity's
 * epoch when `mux_livestreams` changes (creation, stop, or reset). The FE
 * reacts by refetching GetShow so the host's encoder picks up the new
 * stream key without waiting on the 30s failsafe poll.
 */
export interface ShowMuxRotatedDelta extends RealtimeStateDelta {
  readonly type: "mux-rotated";
}

/** Discriminated union for the show-state channel */
export type ShowStateDelta =
  | ShowStateSnapshotDelta
  | ShowRecordingReadyDelta
  | ShowMuxRotatedDelta;

// ---------------------------------------------------------------------------
// Stage-presence channel: fanfest:{showId}:stage-presence
// ---------------------------------------------------------------------------

/**
 * Legacy stage-presence delta on `fanfest:{showId}:stage-presence`.
 * Carries fields that are not persisted in Postgres today: who is on stage,
 * whether fans can join, the simulfest preparation handshake timestamp.
 * This is not part of the Plan 068 stage runtime stream registry and must not
 * be treated as authoritative primary stage state.
 */
export interface StagePresenceDelta extends RealtimeStateDelta {
  readonly type: "stage-presence";
  /** (legacy client-published) */
  readonly currentSpeakerUserId: string | null;
  /** (legacy client-published) */
  readonly canFansJoinStage: boolean;
  /** (legacy client-published) — epoch ms of simulfest preparation handshake, or null */
  readonly hasSetupSimulfestPreparation: number | null;
}

// ---------------------------------------------------------------------------
// Simulcast-state channel: fanfest:{showId}:simulcast-state
// ---------------------------------------------------------------------------

/**
 * Published on `fanfest:{showId}:simulcast-state`.
 * status mirrors the Prisma ShowSimulcastInstanceStatus enum exactly
 * (pending | booted_up | live | ended | failed). There is no "preparing" value.
 */
export interface SimulcastInstanceStateDelta extends RealtimeStateDelta {
  readonly type: "simulcast-state";
  /** (persisted, show_simulcast_instances.id) */
  readonly instanceId: string;
  /** (persisted column show_simulcast_instances.status) */
  readonly status: ShowSimulcastInstanceStatus;
  /** (persisted column show_simulcast_instances.video_variant) */
  readonly variant: "landscape" | "portrait";
  // hls_url is a confidential bearer value (anyone with it can play the stream
  // and bypass authorization). It is intentionally NOT broadcast on this delta.
  // Authenticated clients fetch hls_url via GetShow's hot-path field selection,
  // which is gated by the resolver's authorization rules.
}

// ---------------------------------------------------------------------------
// Quiz channel: fanfest:{showId}:quiz
// ---------------------------------------------------------------------------

/**
 * Published on `fanfest:{showId}:quiz`.
 * The frontend "step" enum (QUESTION | CORRECT_ANSWER | WINNER | LEADERBOARD)
 * is NOT persisted. Step transitions are folded into this channel as
 * QuizTransitionDelta (TICKET-020); the dedicated `quiz-transition-events`
 * channel is kept as zombie per ROLLBACK-SAFETY-PLAN Rule 1.
 */
export interface QuizStateDelta extends RealtimeStateDelta {
  readonly type: "quiz-state";
  /** (matches entityId; persisted, quizzes.id) */
  readonly quizId: string;
  /** (persisted, quiz_questions.id where status=active) */
  readonly currentQuestionId: string | null;
  /** (persisted column quizzes.status) */
  readonly quizStatus: ContestStatus;
}

/**
 * Published on the quiz transition runtime stream.
 * Carries the current transition rendered to all fans.
 * Not persisted in Postgres.
 * Folded into the quiz channel by TICKET-020; the legacy
 * `quiz-transition-events` channel is kept as zombie per ROLLBACK-SAFETY-PLAN Rule 1.
 */
export interface QuizTransitionDelta extends RealtimeStateDelta {
  readonly type: "quiz-transition";
  /** (matches entityId; quizzes.id) */
  readonly quizId: string;
  /** (runtime) */
  readonly transition: QuizTransition;
}

/**
 * Public-profile winner entry carried by {@link QuizWinnerDelta}.
 * Mirrors the public subset of {@link RaffleWinnerEntry} — only the fields
 * needed to render the quiz `quiz_winner` reveal card (`Avatar` + the winner
 * name). NO `channelMembershipPermissionMasks` / `scorePosition`: the quiz
 * winner card does not style by membership tier, unlike the raffle card.
 *
 * `username` / `avatar` are public profile fields per the existing
 * `RaffleWinnerDelta` precedent.
 */
export interface QuizWinnerEntry {
  /** (persisted, users.id) */
  readonly userId: string;
  /** (persisted, users.username) */
  readonly username: string;
  /** (persisted, users.avatar — public profile field) */
  readonly avatar: string | null;
}

/**
 * Published on `fanfest:{channelId}:quiz-list` after a quiz ends, carrying the
 * winner-user payload directly so already-connected fans / simulfest viewers
 * can render the `quiz_winner` reveal card without refetching
 * GetQuizzesForChannel (an uncacheable per-user list query — a refetch fan-out
 * at quiz-end would be a thundering herd).
 *
 * Discriminated by type === "quiz-winner" within the always-on `quiz-list`
 * channel — every consumer of that channel MUST switch on `delta.type`
 * (Pattern B). Mirrors {@link RaffleWinnerDelta}.
 *
 * Late joiners are covered by their initial GetQuizzesForChannel load (which
 * reads winner_users straight from Postgres); the delta only seeds clients
 * that were already connected when the quiz ended.
 */
export interface QuizWinnerDelta extends RealtimeStateDelta {
  readonly type: "quiz-winner";
  /** (matches entityId; persisted, quizzes.id) */
  readonly quizId: string;
  readonly winnerUsers: ReadonlyArray<QuizWinnerEntry>;
}

// ---------------------------------------------------------------------------
// Raffle channel: fanfest:{showId}:raffle
// ---------------------------------------------------------------------------

/**
 * Published on `fanfest:{showId}:raffle`.
 * Carries persisted raffle state. Step and slot transitions are also published
 * here (TICKET-019 fold — formerly on `raffle-step-events` / `raffle-slot-events`).
 */
export interface RaffleStateDelta extends RealtimeStateDelta {
  readonly type: "raffle-state";
  /** (matches entityId; persisted, raffles.id) */
  readonly raffleId: string;
  /** (persisted column raffles.status) */
  readonly raffleStatus: ContestStatus;
  /** (persisted aggregate, count of raffle entries) */
  readonly entryCount: number;
}

export interface RaffleWinnerEntry {
  /** (persisted, users.id) */
  readonly userId: string;
  /** (persisted, users.username) */
  readonly username: string;
  /** (persisted, users.avatar — public profile field) */
  readonly avatar: string | null;
  /** (persisted, raffle_winner_users.score_position) */
  readonly scorePosition: number;
  /** Channel-membership permission masks for the winner's user_channel_memberships rows
   * on the active channel, used by the FE to drive winner-card styling. */
  readonly channelMembershipPermissionMasks: ReadonlyArray<number>;
}

export interface RaffleRuntimeState {
  readonly raffleId: string | null;
  readonly status: RaffleRuntimeStatus;
  readonly step: RaffleStep;
  readonly slotState: RaffleSlotState | null;
  readonly winnerUsers: ReadonlyArray<RaffleWinnerEntry>;
  readonly phaseStartedAtEpochMs: number | null;
  readonly phaseDurationMs: number | null;
}

/**
 * Published on `fanfest:{showId}:raffle` after a raffle ends, carrying
 * the winner-user payload directly so fans/simulfest viewers can render
 * the winner without refetching GetRafflesForChannel.
 *
 * Discriminated by type === "raffle-winner" within the raffle channel.
 * Mirrors the chat-translation pattern (Plan 057) — local reactive state
 * on the FE, no cache patching.
 *
 * Plan 068 stream model: ordered by `streamId` via EpochService.nextStream.
 * Visibility is gated by the primary-stage owner, not by a shared raffle
 * entity epoch.
 */
export interface RaffleWinnerDelta extends RealtimeStateDelta {
  readonly type: "raffle-winner";
  /** (matches entityId; persisted, raffles.id) */
  readonly raffleId: string;
  readonly winnerUsers: ReadonlyArray<RaffleWinnerEntry>;
}

/**
 * Published on the raffle step runtime stream.
 * Carries the current raffle step transition so fans see the animation state.
 * Not persisted in Postgres.
 *
 * Folded into the raffle channel by TICKET-019. Legacy `raffle-step-events`
 * channel kept as zombie per ROLLBACK-SAFETY-PLAN Rule 1.
 *
 * Plan 068 stream model: ordered by `streamId` via EpochService.nextStream.
 */
export interface RaffleStepDelta extends RealtimeStateDelta {
  readonly type: "raffle-step";
  /** (matches entityId; raffles.id) */
  readonly raffleId: string;
  /** Current renderable raffle phase, or null after clearing/stable idle. */
  readonly step: RaffleStep;
  /** Server epoch ms when the current phase started, or null if no timed phase. */
  readonly phaseStartedAtEpochMs: number | null;
  /** Duration of the current timed phase, or null if no timed phase. */
  readonly phaseDurationMs: number | null;
}

/**
 * Published on the raffle slot runtime stream.
 * Carries the slot-machine animation state so fans see the slot animation.
 * Not persisted in Postgres.
 *
 * Folded into the raffle channel by TICKET-019. Legacy `raffle-slot-events`
 * channel kept as zombie per ROLLBACK-SAFETY-PLAN Rule 1.
 *
 * Plan 068 stream model: ordered by `streamId` via EpochService.nextStream.
 */
export interface RaffleSlotDelta extends RealtimeStateDelta {
  readonly type: "raffle-slot";
  /** (matches entityId; raffles.id) */
  readonly raffleId: string;
  /** (runtime) */
  readonly slotState: RaffleSlotState;
}

// ---------------------------------------------------------------------------
// Quiz list-changed channel: fanfest:{channelId}:quiz-list
// ---------------------------------------------------------------------------

/**
 * Published on `fanfest:{channelId}:quiz-list` by the API whenever the list of
 * quizzes for a channel changes (created, edited, deleted, question
 * added/updated/deleted). Consumers react by refetching GetQuizzesForChannel.
 * entityId = channelId so the epoch map keys by channel, not by individual quiz.
 */
export interface QuizListChangedDelta extends RealtimeStateDelta {
  readonly type: "quiz-list-changed";
  /** Channel whose quiz list changed (used as entityId for epoch keying) */
  readonly channelId: string;
  /**
   * - `created` / `edited` / `deleted` — list-shape changes; consumers refetch.
   * - `activated` — host has started this quiz; receivers should adopt
   *   `currentQuizId = quizId` so the lazy quiz channel attaches and downstream
   *   `quiz-state` / `quiz-transition` deltas are received. Carries the activation
   *   signal on the always-on `quiz-list` channel, avoiding the late-join
   *   chicken-and-egg where the lazy `quiz` channel isn't subscribed until
   *   `currentQuizId` is set.
   * - `deactivated` — host has ended this quiz; receivers clear `currentQuizId`
   *   if it matches `quizId`.
   */
  readonly changeType:
    | "created"
    | "deleted"
    | "edited"
    | "activated"
    | "deactivated";
  readonly quizId: string;
}

// ---------------------------------------------------------------------------
// Raffle list-changed channel: fanfest:{channelId}:raffle-list
// ---------------------------------------------------------------------------

/**
 * Published on `fanfest:{channelId}:raffle-list` by the API whenever the list
 * of raffles for a channel changes (created, edited, deleted).
 * Consumers react by refetching GetRafflesForChannel.
 * entityId = channelId so the epoch map keys by channel, not by individual raffle.
 */
export interface RaffleListChangedDelta extends RealtimeStateDelta {
  readonly type: "raffle-list-changed";
  /** Channel whose raffle list changed (used as entityId for epoch keying) */
  readonly channelId: string;
  /**
   * - `created` / `edited` / `deleted` — list-shape changes; consumers refetch.
   * - `activated` — host has started this raffle; receivers should adopt
   *   `currentRaffleId = raffleId` so the lazy raffle channel attaches and
   *   downstream `raffle-state` / `raffle-step` / `raffle-slot` deltas are
   *   received. Carries the activation signal on the always-on `raffle-list`
   *   channel, avoiding the late-join chicken-and-egg where the lazy `raffle`
   *   channel isn't subscribed until `currentRaffleId` is set.
   * - `deactivated` — host has ended this raffle; receivers clear
   *   `currentRaffleId` if it matches `raffleId`.
   */
  readonly changeType:
    | "created"
    | "deleted"
    | "edited"
    | "activated"
    | "deactivated";
  readonly raffleId: string;
}

// ---------------------------------------------------------------------------
// Reactions channel: fanfest:{showId}:reactions-v2
// ---------------------------------------------------------------------------

/**
 * Per-reaction entry in a batched reactions publish.
 * `clientTimestampMs` is set by the sender's client at click time and is
 * load-bearing for the FE local-sender dedup: the receiver matches by
 * (userId, reactionType, clientTimestampMs ± 2000ms).
 */
export interface ReactionEvent {
  readonly userId: string;
  readonly reactionType: string;
  readonly clientTimestampMs: number;
}

/**
 * Published on `fanfest:{showId}:reactions-v2` by the API's
 * `ReactionsRealtimeService` in 500 ms batches.
 * `entityId` = showId. Reactions are ephemeral; rewind is 0 by design.
 */
export interface ReactionsBatchDelta extends RealtimeStateDelta {
  readonly type: "reactions-batch";
  readonly reactions: ReactionEvent[];
  readonly batchEpochMs: number;
}

// ---------------------------------------------------------------------------
// Agenda channel: fanfest:{showId}:agenda
// ---------------------------------------------------------------------------

/**
 * Published on `fanfest:{showId}:agenda`.
 */
export interface AgendaStateDelta extends RealtimeStateDelta {
  readonly type: "agenda-state";
  /** (persisted column show_agenda_items.id, indirectly via active item) */
  readonly currentAgendaItemId: string | null;
  /** (runtime) */
  readonly isFullScreen: boolean;
}

// ---------------------------------------------------------------------------
// Layout channel: fanfest:{showId}:layout
// ---------------------------------------------------------------------------

/**
 * Published on `fanfest:{showId}:layout`.
 * contentPosition is runtime state today; may persist in a future ticket.
 */
export interface LayoutStateDelta extends RealtimeStateDelta {
  readonly type: "layout-state";
  /** (runtime today; may persist in a future ticket) */
  readonly contentPosition: LayoutContentPosition;
}

// ---------------------------------------------------------------------------
// Update-trigger delta — TICKET-013
// Carried on the consolidated show-state-v2 channel (no dedicated channel).
// ---------------------------------------------------------------------------

/**
 * Published on `fanfest:{showId}:show-state-v2`.
 * Host-broadcast escape-hatch refetch trigger. Carries `lastUpdateTimeEpoch`
 * for parity with the legacy `IUpdatesTriggerState` payload. Tied to the
 * show entity for epoch keying.
 */
export interface ShowUpdateTriggerDelta extends RealtimeStateDelta {
  readonly type: "update-trigger";
  /** ms since epoch of the server-authored update trigger */
  readonly lastUpdateTimeEpoch: number;
}

// ---------------------------------------------------------------------------
// Offer-state delta — TICKET-018
// Host-set offer/popup overlay state. Folded into show-state-v2.
// ---------------------------------------------------------------------------

export interface ShowOfferPayload {
  readonly id: string;
  readonly url: string;
  readonly image_url: string;
  readonly description: string;
}

/**
 * Published on `fanfest:{showId}:show-state-v2` (folded from the legacy
 * `fanfest:{showId}:offer-state` channel by TICKET-018).
 * Null offer clears the active overlay.
 */
export interface ShowOfferPublishedDelta extends RealtimeStateDelta {
  readonly type: "offer-state";
  readonly offer: ShowOfferPayload | null;
}

// ---------------------------------------------------------------------------
// Media-state delta — TICKET-018
// Host-set media tile state (image URL, video, etc.). Folded into show-state-v2.
// ---------------------------------------------------------------------------

/**
 * Published on `fanfest:{showId}:show-state-v2` (folded from the legacy
 * `fanfest:{showId}:media` channel by TICKET-018).
 * Null mediaUrl clears the media tile.
 */
export interface ShowMediaPublishedDelta extends RealtimeStateDelta {
  readonly type: "media-state";
  readonly mediaUrl: string | null;
}

// ---------------------------------------------------------------------------
// show-state-v2 unified channel — TICKET-013 + TICKET-018
// Runtime stage deltas consolidate into one unified channel carrying a
// discriminated union. Each variant retains its own DDB epoch counter.
// ---------------------------------------------------------------------------

/**
 * Discriminated union of every delta carried on `fanfest:{showId}:show-state-v2`.
 * The FE subscribes once with this generic and branches on `delta.type` inside
 * `applyDelta`. Each variant carries a `streamId` so independently-versioned
 * surfaces don't step on each other when they share the channel.
 */
export type ShowStateV2Delta =
  | PrimaryStageStateDelta
  | ShowStateDelta
  | LayoutStateDelta
  | AgendaStateDelta
  | SimulcastInstanceStateDelta
  | ShowUpdateTriggerDelta
  | ShowOfferPublishedDelta
  | ShowMediaPublishedDelta;

// ===========================================================================
// Stage presentation plans (slot-based, server-authored)
// ===========================================================================
//
// A presentation plan is the server-authored render program for ONE stage slot.
// The backend commits a complete plan; every client (host, fan, simulfest)
// derives the current visual phase purely from the plan and server time —
// `phase = phaseAt(serverNowEpochMs - serverStartedAtEpochMs, phases)`.
//
// Clients NEVER publish the next phase and NEVER drive timed transitions. Time
// passage is a pure projection over committed server state: a missed local
// repaint timer only delays a render until the next tick/snapshot, it can never
// change committed stage state. Ordering and supersession are by the runtime
// stream `epoch`, NEVER by comparing `planId`. The backend is the sole
// authority for `engagementClosesAtEpochMs`; frontend controls are advisory.
//
// This is a forward-only contract. There are no old-client-compat fields
// and no duplicate legacy plan/delta shapes.

/**
 * Stage slot identifiers. Stage content is addressed by slot, not by content
 * type, so conflict checks never hardcode "quiz vs raffle vs media vs offer".
 * Additional slots are added by extending this union and the registry — no
 * per-component conflict-check rewrites.
 */
export type StageSlotId = "content" | "agenda" | "layout";

/** Runtime list of every {@link StageSlotId}. Source of truth for guards. */
export const STAGE_SLOT_IDS = ["content", "agenda", "layout"] as const;

/**
 * Kinds of content that can occupy a stage slot. Used by
 * {@link StageContentTarget} and the slot registry's allowed-content mapping.
 */
export type StageContentKind =
  | "quiz"
  | "raffle"
  | "media"
  | "offer"
  | "agenda"
  | "layout";

/**
 * Discriminated content target a presentation plan renders. Carries IDs and
 * public scalars only — never full query objects, PII, or stream URLs.
 */
export type StageContentTarget =
  | {
      readonly kind: "quiz";
      readonly quizId: string;
      /** Active question, or null when no question is currently active. */
      readonly questionId: string | null;
    }
  | {
      readonly kind: "raffle";
      readonly raffleId: string;
    }
  | { readonly kind: "media"; readonly mediaUrl: string }
  | { readonly kind: "offer"; readonly offer: ShowOfferPayload }
  | {
      readonly kind: "agenda";
      readonly currentAgendaItemId: string | null;
      readonly isFullScreen: boolean;
    }
  | {
      readonly kind: "layout";
      readonly contentPosition: LayoutContentPosition;
    };

/**
 * Registry entry describing a single stage slot. The registry — not component
 * conditionals — is the single source of truth for slot exclusivity and which
 * content kinds a slot accepts.
 */
export interface StageSlotRegistryEntry {
  readonly slotId: StageSlotId;
  /**
   * When true, only one active plan may occupy the slot at a time
   * (`content`). Non-exclusive slots (`agenda`, `layout`) are auxiliary
   * runtime streams that do not block the exclusive content slot.
   */
  readonly exclusive: boolean;
  /** Content kinds this slot accepts. */
  readonly allowedContentKinds: ReadonlyArray<StageContentKind>;
  /** Human-readable description for tooling/docs. */
  readonly description: string;
}

/**
 * The stage slot registry, keyed by {@link StageSlotId}. Exclusivity is a
 * registry attribute, never a hardcoded special case. Future slots are added
 * here plus their allowed-content mapping — no new per-component conflict
 * checks.
 */
export const STAGE_SLOT_REGISTRY: Readonly<
  Record<StageSlotId, StageSlotRegistryEntry>
> = {
  content: {
    slotId: "content",
    exclusive: true,
    allowedContentKinds: ["quiz", "raffle", "media", "offer"],
    description:
      "Main audience-side stage panel. Exclusive — quiz, raffle, media, " +
      "and offer content all conflict on this slot.",
  },
  agenda: {
    slotId: "agenda",
    exclusive: false,
    allowedContentKinds: ["agenda"],
    description:
      "Regular-show agenda topic pointer and fullscreen flag. Non-exclusive " +
      "auxiliary runtime stream; does not occupy the exclusive content slot.",
  },
  layout: {
    slotId: "layout",
    exclusive: false,
    allowedContentKinds: ["layout"],
    description:
      "Stage layout / content order. Non-exclusive auxiliary runtime stream.",
  },
};

/**
 * Typed presentation-phase names. A shared string-literal union, never
 * arbitrary strings — this union MUST land before any guard or projector
 * switch is written.
 *
 * `quiz_ready` is intentionally NOT a member: a quiz occupies the `content`
 * slot only when a question is active or a reveal phase is in flight. There is
 * no "quiz up, no question" slot-occupying state — that stage is simply empty.
 *
 * Raffle phase names are TRUTHFUL of their engagement semantics — the name
 * tells you the lifecycle state and (with the `engagementOpen` flag) whether
 * fans may enter. The corrected plan-078 raffle model:
 *
 * - `raffle_entry` — the raffle is *Activated*. Entry is OPEN
 *   (`engagementOpen: true`); fans join. Committed by `activateRaffle`. Replaces
 *   the misleading `raffle_ready`, whose name implied entry was not yet open.
 * - `raffle_drawing` — the raffle is *Started*. The slot-machine suspense runs
 *   and entry is CLOSED (`engagementOpen: false`); the phase holds until the
 *   host ends the raffle. Committed by `startRaffleCommand`. Replaces the
 *   misleading `entry`, whose name implied entry was open in the Started state,
 *   and re-homes the slot-machine here (the End reveal no longer has a leading
 *   draw phase — it hard-cuts straight to `raffle_winner`).
 * - `raffle_winner` / `raffle_reward` / `raffle_reward_generic` — the End
 *   reveal beats. `raffle_reward` is used only when the winner's prize has a
 *   non-empty image; `raffle_reward_generic` otherwise. Engagement is closed
 *   for every End-reveal phase.
 *
 * Engagement is OPEN during *Activated* (`raffle_entry`) and CLOSED once
 * *Started* (`raffle_drawing`) — a join gate or projector MUST consult the
 * `engagementOpen` flag / `effectiveSlot().engagementOpen`, never the phase
 * name.
 */
export type StagePresentationPhaseName =
  // Quiz / poll
  | "question"
  | "correct_answer"
  | "poll_answer"
  | "leaderboard"
  | "quiz_winner"
  // Raffle
  | "raffle_entry"
  | "raffle_drawing"
  | "raffle_winner"
  | "raffle_reward"
  | "raffle_reward_generic"
  // Static content
  | "display"
  | "empty"
  // Agenda / layout
  | "agenda_state"
  | "layout_state";

/** Runtime list of every {@link StagePresentationPhaseName}. */
export const STAGE_PRESENTATION_PHASE_NAMES = [
  "question",
  "correct_answer",
  "poll_answer",
  "leaderboard",
  "quiz_winner",
  "raffle_entry",
  "raffle_drawing",
  "raffle_winner",
  "raffle_reward",
  "raffle_reward_generic",
  "display",
  "empty",
  "agenda_state",
  "layout_state",
] as const;

/**
 * Compact, ID-only phase payload. Discriminated by an explicit literal `kind`
 * so the TypeGraphQL `resolveType` and the frontend `__typename` switch are
 * both unambiguous.
 *
 * Payloads carry IDs, counts, status enums, and public fields only — never
 * full query objects, private profile data, stream URLs, or free-text content.
 * A whole serialized plan must stay under 16 KB (enforced backend-side in
 * `StageRuntimeStoreService`).
 */
export type StagePresentationPhasePayload =
  | {
      readonly kind: "quiz";
      readonly quizId: string;
      /** Question this phase renders, or null for quiz-level phases. */
      readonly questionId: string | null;
    }
  | {
      readonly kind: "raffle";
      readonly raffleId: string;
    }
  | {
      readonly kind: "static";
    };

/**
 * One ordered phase within a presentation plan. Offsets are relative to the
 * plan's `serverStartedAtEpochMs`; the absolute phase start is
 * `serverStartedAtEpochMs + startsAtMs`.
 */
export interface StagePresentationPhase {
  /** Phase name from the shared {@link StagePresentationPhaseName} union. */
  readonly phase: StagePresentationPhaseName;
  /** Offset (ms) from `serverStartedAtEpochMs` when this phase begins. */
  readonly startsAtMs: number;
  /**
   * Duration (ms) of this phase. `null` means the phase is terminal / static
   * (no automatic advance — it lasts until the plan is superseded or cleared).
   */
  readonly durationMs: number | null;
  /** Compact ID-only payload; `null` for payload-free phases such as `empty`. */
  readonly payload: StagePresentationPhasePayload | null;
  /**
   * When true, fan engagement (quiz answers, raffle entries) is open for this
   * phase. The authoritative deadline is the plan's
   * `engagementClosesAtEpochMs`; this flag only marks which phases are
   * engagement phases at all.
   */
  readonly engagementOpen?: boolean;
}

/** Lifecycle status of a presentation plan. */
export type StagePresentationPlanStatus =
  | "active"
  | "transitioning"
  | "ended"
  | "empty";

/**
 * The server-authored render program for one stage slot.
 *
 * `planId` is the creating stage command id, never a commit id — it is a
 * stable correlation handle only. Ordering and stale-replay rejection are by
 * the runtime stream `epoch`, never by comparing `planId`.
 *
 * `serverCommittedAtEpochMs` is the backend write/ordering time;
 * `serverStartedAtEpochMs` is when the plan becomes visible. They are usually
 * equal, but a future scheduled slot may be committed before it starts —
 * clients must not render a plan as active until
 * `serverNowEpochMs >= serverStartedAtEpochMs`.
 */
export interface StagePresentationPlan {
  /** Slot this plan owns. */
  readonly slotId: StageSlotId;
  /** The creating stage command id. NOT a commit id; ordering is by `epoch`. */
  readonly planId: string;
  /** Integer contract version; starts at 1. */
  readonly planVersion: number;
  /** Discriminated content target the plan renders. */
  readonly content: StageContentTarget;
  /** Lifecycle status of the plan. */
  readonly status: StagePresentationPlanStatus;
  /**
   * Identifier of the host that committed this plan. Required so a client can
   * name who superseded content for the stomped-host notification
   * ("Host B replaced your quiz with a raffle"). Supersession by a different
   * host is an authorized operation, not an error.
   */
  readonly committedByHostId: string;
  /** Backend write / ordering time (UTC epoch ms). */
  readonly serverCommittedAtEpochMs: number;
  /** When the plan becomes visible (UTC epoch ms). */
  readonly serverStartedAtEpochMs: number;
  /**
   * Last epoch ms the plan can occupy the slot. `null` means static / no
   * automatic expiry — the slot stays occupied until the plan is cleared or
   * superseded.
   */
  readonly validUntilEpochMs: number | null;
  /**
   * Optional epoch ms after which fan answers/entries are rejected. The
   * backend is the only authority for this value.
   */
  readonly engagementClosesAtEpochMs?: number | null;
  /** Ordered phase definitions. */
  readonly phases: ReadonlyArray<StagePresentationPhase>;
}

/** Host-visible lock state for a stage slot. */
export type StageSlotLockState = "pending" | "occupied" | "empty";

/**
 * Host-visible slot lock metadata. Lets any host see WHY a control is disabled
 * without inferring it from another host's local UI. Lock state is derived
 * (pending command operations + committed plans evaluated at server time) —
 * there is no separate manually managed occupancy mutex.
 */
export interface StageSlotLock {
  readonly slotId: StageSlotId;
  readonly lockState: StageSlotLockState;
  /** Plan id occupying the slot, or null when empty / pending-only. */
  readonly planId: string | null;
  /** Stage command id holding the slot, or null when none. */
  readonly commandId: string | null;
  /**
   * Identifier of the host that committed the occupying plan, or null. Mirrors
   * {@link StagePresentationPlan.committedByHostId} so a consumer can resolve
   * the committing host's identity directly from lock metadata.
   */
  readonly committedByHostId: string | null;
  /** Epoch ms the lock is held until, or null for static / no expiry. */
  readonly lockedUntilEpochMs: number | null;
}

/**
 * Dedicated presentation-plan delta. A distinct delta type — it is NOT folded
 * onto `PrimaryStageStateDelta` or `ShowStateV2Delta`. It rides the dedicated
 * `fanfest:{showId}:stage-plan` channel and carries a slot-keyed plan shape so
 * `delta.type` filtering stays unambiguous.
 *
 * The envelope exposes `committedByHostId` (in addition to the nested
 * `plan.committedByHostId`) so a consumer can resolve the committing host
 * without reading into the plan body.
 */
export interface StageSlotStateDelta extends RealtimeStateDelta {
  readonly type: "stage-slot-state";
  readonly showId: string;
  /** Slot this delta updates. */
  readonly slotId: StageSlotId;
  /**
   * The committed plan for the slot, or `null` when the slot has been
   * explicitly cleared to empty.
   */
  readonly plan: StagePresentationPlan | null;
  /**
   * Identifier of the committing host. Duplicated from `plan.committedByHostId`
   * for clear-to-null deltas the host id is the host that issued the clear.
   */
  readonly committedByHostId: string;
  /** Mirrors `plan.validUntilEpochMs`; `null` for static plans or clears. */
  readonly validUntilEpochMs: number | null;
}

/**
 * Effective stage-slot state at a given server time — the pure projection of a
 * presentation plan. Computed identically by backend (command conflict checks)
 * and frontend (rendering) via {@link effectiveSlot}.
 */
export type EffectiveSlotState =
  | {
      /** No plan, or the plan has expired / cleared — the slot is empty. */
      readonly kind: "empty";
    }
  | {
      /**
       * The plan exists but `serverNowEpochMs < serverStartedAtEpochMs` — it
       * is scheduled, not yet live, and must not render as live content.
       */
      readonly kind: "scheduled";
      readonly plan: StagePresentationPlan;
    }
  | {
      /** The slot is actively rendering `phase` at `phaseOffsetMs`. */
      readonly kind: "active";
      readonly plan: StagePresentationPlan;
      readonly phase: StagePresentationPhase;
      /** Index of `phase` within `plan.phases`. */
      readonly phaseIndex: number;
      /** Elapsed ms since `serverStartedAtEpochMs`. */
      readonly phaseOffsetMs: number;
      /**
       * Whether fan engagement is currently open: the phase is an engagement
       * phase AND the plan's `engagementClosesAtEpochMs` has not passed.
       */
      readonly engagementOpen: boolean;
    };

/**
 * Snapshot of a single stage slot — the slot's committed plan, its effective
 * state at `serverNowEpochMs`, and host-visible lock metadata.
 */
export interface StageSlotSnapshot {
  readonly slotId: StageSlotId;
  /** Committed plan for the slot, or null when empty. */
  readonly plan: StagePresentationPlan | null;
  /** Effective state projected at the snapshot's `serverNowEpochMs`. */
  readonly effective: EffectiveSlotState;
  /** Host-visible lock metadata. */
  readonly lock: StageSlotLock;
}

// ---------------------------------------------------------------------------
// Stage-plan channel: fanfest:{showId}:stage-plan
// ---------------------------------------------------------------------------
//
// New dedicated subscribe-only channel for presentation-plan deltas.
// Plan deltas MUST publish here and MUST NOT be folded onto `show-state-v2` or
// the legacy `:quiz` / `:raffle` channels. Because the rollout is forward-only,
// an old-FE client during the update-notification window simply does not
// receive the new deltas (stale stage, no crash) rather than mishandling an
// unknown delta shape. Browser publish must stay denied on this channel.

// ---------------------------------------------------------------------------
// Stage command operations and runtime snapshot
// ---------------------------------------------------------------------------

export type StageCommandStatus =
  | "pending"
  | "committed"
  | "failed"
  | "timed-out";

export type StageCommandRepairStatus =
  | "none"
  | "needed"
  | "in_progress"
  | "repaired"
  | "failed";

export type StageCommandTarget =
  | {
      readonly kind: "quiz";
      readonly action: "activate" | "end";
      readonly quizId: string;
    }
  | {
      readonly kind: "quiz";
      readonly action:
        | "activate-question"
        | "deactivate-question"
        | "end-question";
      readonly quizId: string;
      readonly questionId: string;
    }
  | {
      readonly kind: "quiz";
      readonly action: "transition";
      readonly quizId: string;
      readonly transition: QuizTransition;
    }
  | {
      readonly kind: "raffle";
      readonly action: "activate" | "start" | "end" | "deactivate";
      readonly raffleId: string;
    }
  | {
      readonly kind: "raffle";
      readonly action: "slot";
      readonly raffleId: string;
      readonly slotState: RaffleSlotState;
    }
  | {
      readonly kind: "raffle";
      readonly action: "step";
      readonly raffleId: string;
      readonly step: RaffleStep;
    }
  | {
      readonly kind: "media";
      readonly mediaUrl: string | null;
    }
  | {
      readonly kind: "offer";
      readonly offer: ShowOfferPayload | null;
    }
  | {
      readonly kind: "layout";
      readonly contentPosition: LayoutContentPosition;
    }
  | {
      readonly kind: "agenda";
      readonly currentAgendaItemId: string | null;
      readonly isFullScreen: boolean;
    }
  | {
      readonly kind: "show-update";
    }
  | {
      readonly kind: "reset-stage";
    };

export interface StageCommandOperation {
  readonly commandId: string;
  readonly showId: string;
  readonly hostUserId: string;
  readonly target: StageCommandTarget;
  readonly status: StageCommandStatus;
  readonly startedAtEpochMs: number;
  readonly updatedAtEpochMs: number;
  readonly timeoutAtEpochMs: number;
  readonly intendedStreams: ReadonlyArray<string>;
  readonly committedEnvelopes: ReadonlyArray<RealtimeStateDelta>;
  readonly failureCode: string | null;
  readonly failureMessage: string | null;
  readonly repairStatus: StageCommandRepairStatus;
  readonly retryCount: number;
}

export interface RuntimeStreamEnvelope<
  TDelta extends RealtimeStateDelta = RealtimeStateDelta,
> {
  readonly streamId: string;
  readonly epoch: number;
  readonly delta: TDelta;
  readonly updatedAtEpochMs: number;
}

/**
 * Slot-keyed show runtime snapshot. Recovery surface for rejoin,
 * refresh, late join, Ably rewind, hidden-tab return, and simulfest restart —
 * every actor converges to the same effective phase from `slots` plus
 * `serverNowEpochMs`.
 *
 * `primaryStageSurface` is intentionally removed: it must not remain as a
 * production compatibility surface or drive new correctness logic. Effective
 * emptiness is derived per-slot from the plan payload, never from a
 * denormalized surface that could re-materialise after expiry.
 *
 * `serverNowEpochMs` is UTC epoch milliseconds — clients re-derive their clock
 * offset from it and project phases without timezone or ISO-parse ambiguity.
 */
export interface ShowRuntimeSnapshot {
  readonly showId: string;
  /** Server time when the snapshot was produced (UTC epoch ms). */
  readonly serverNowEpochMs: number;
  /** Effective + lock state for every stage slot, keyed by slot id. */
  readonly slots: Readonly<Record<StageSlotId, StageSlotSnapshot>>;
  /**
   * Host-visible slot lock metadata. Lets a host see why a control is locked
   * without inferring it from another host's local UI.
   */
  readonly slotLocks: ReadonlyArray<StageSlotLock>;
  /** Raw runtime streams — retained for debugging/tests only. */
  readonly streams: ReadonlyArray<RuntimeStreamEnvelope>;
  readonly hostOperations?: ReadonlyArray<StageCommandOperation>;
}

// ---------------------------------------------------------------------------
// Channel-state channel: fanfest:{channelId}:channel-state
// ---------------------------------------------------------------------------

/**
 * A single avatar entry in the recent-avatars ring.
 * Both username and avatar are public profile fields per existing RaffleWinnerDelta precedent.
 */
export interface RecentAvatarEntry {
  readonly userId: string;
  readonly username: string;
  readonly avatar: string | null;
  readonly enteredAtEpochMs: number;
}

/**
 * Published on `fanfest:{channelId}:channel-state` via HotStateWriter.
 * Carries the 5 most recently active authenticated users in the channel,
 * aggregated server-side. Replaces the chat presence fanout for avatar display.
 */
export interface RecentAvatarsDelta extends RealtimeStateDelta {
  readonly type: "recent-avatars";
  readonly channelId: string;
  readonly recentAvatars: RecentAvatarEntry[];
}

// ---------------------------------------------------------------------------
// User-events channel: api-events:user:{userId}
// ---------------------------------------------------------------------------

/**
 * Published on `api-events:user:{userId}` (topic "state") by the API's
 * LoyaltyRealtimeService when a user earns loyalty points.
 * This is NOT a RealtimeStateDelta — it carries no streamId/epoch/commitId.
 * It is a simple event message scoped to a single authenticated user.
 */
export interface LoyaltyPointsUpdateEvent {
  /** Points earned in this event (may be negative for deductions) */
  readonly points: number;
  /** Human-readable label for the earning event (e.g. "Joined Show") */
  readonly message?: string;
  /** ID of the loyalty experience that triggered the award */
  readonly experienceId?: string | null;
  /** Type of the loyalty experience (e.g. "JOIN_SHOW", "SENT_CHAT") */
  readonly experienceType?: string | null;
  /** Whether a points multiplier was applied to this award */
  readonly hasMultiplierApplied: boolean;
}

// ---------------------------------------------------------------------------
// Union of all delta types
// ---------------------------------------------------------------------------

export type AnyRealtimeStateDelta =
  | PrimaryStageStateDelta
  | ShowStateDelta
  | AgendaStateDelta
  | StagePresenceDelta
  | SimulcastInstanceStateDelta
  | QuizStateDelta
  | QuizTransitionDelta
  | QuizWinnerDelta
  | QuizListChangedDelta
  | RaffleStateDelta
  | RaffleWinnerDelta
  | RaffleStepDelta
  | RaffleSlotDelta
  | RaffleListChangedDelta
  | LayoutStateDelta
  | ShowUpdateTriggerDelta
  | ShowOfferPublishedDelta
  | ShowMediaPublishedDelta
  | StageSlotStateDelta
  | RecentAvatarsDelta
  | ReactionsBatchDelta;

// ---------------------------------------------------------------------------
// Channel name helpers
// ---------------------------------------------------------------------------

/**
 * Canonical Ably channel name builders.
 * Backend and frontend both import from @f3/shared — channel strings can
 * never drift between the two ends.
 */
export const REALTIME_CHANNELS = {
  /** Zombie (TICKET-013) — folded into `showStateV2`. Kept exported per ROLLBACK-SAFETY-PLAN Rule 1; cleanup PR removes ≥7 days post-launch. */
  showState: (showId: string) => `fanfest:${showId}:show-state`,
  /** Zombie (TICKET-013) — folded into `showStateV2`. */
  simulcastState: (showId: string) => `fanfest:${showId}:simulcast-state`,
  quiz: (showId: string) => `fanfest:${showId}:quiz`,
  /** Zombie (TICKET-020) — quiz transitions folded into `quiz`. Kept per ROLLBACK-SAFETY-PLAN Rule 1; cleanup PR removes after ≥1 stable show. */
  quizTransitionEvents: (showId: string) =>
    `fanfest:${showId}:quiz-transition-events`,
  /**
   * Channel-scoped quiz-list channel — keyed by `channelId`, NOT show id.
   * Quizzes are channel-scoped; keying by channel id lets every viewer of any
   * show on the channel (and the channel page) subscribe to one channel and
   * removes the channel->live-show lookup from the publish path. Mirrors
   * `CHANNELS.quizList` in realtime-channels.ts.
   */
  quizList: (channelId: string) => `fanfest:${channelId}:quiz-list`,
  raffle: (showId: string) => `fanfest:${showId}:raffle`,
  /**
   * Channel-scoped raffle-list channel — keyed by `channelId`, NOT show id.
   * Raffles are channel-scoped; keying by channel id lets every viewer of any
   * show on the channel (and the channel page) subscribe to one channel and
   * removes the channel->live-show lookup from the publish path. Mirrors
   * `CHANNELS.raffleList` in realtime-channels.ts.
   */
  raffleList: (channelId: string) => `fanfest:${channelId}:raffle-list`,
  /** Zombie (TICKET-019) — folded into `raffle`. Kept per ROLLBACK-SAFETY-PLAN Rule 1; cleanup PR removes after ≥1 stable show. */
  raffleStepEvents: (showId: string) => `fanfest:${showId}:raffle-step-events`,
  /** Zombie (TICKET-019) — folded into `raffle`. Kept per ROLLBACK-SAFETY-PLAN Rule 1; cleanup PR removes after ≥1 stable show. */
  raffleSlotEvents: (showId: string) => `fanfest:${showId}:raffle-slot-events`,
  /** Zombie (TICKET-013) — folded into `showStateV2`. */
  layout: (showId: string) => `fanfest:${showId}:layout`,
  /** Zombie (TICKET-013) — folded into `showStateV2`. */
  agenda: (showId: string) => `fanfest:${showId}:agenda`,
  /** Zombie (TICKET-018) — folded into `showStateV2`. Kept exported per ROLLBACK-SAFETY-PLAN Rule 1; cleanup PR removes ≥7 days post-launch. */
  offerState: (showId: string) => `fanfest:${showId}:offer-state`,
  /** Zombie (TICKET-018) — folded into `showStateV2`. Kept exported per ROLLBACK-SAFETY-PLAN Rule 1; cleanup PR removes ≥7 days post-launch. */
  media: (showId: string) => `fanfest:${showId}:media`,
  /**
   * TICKET-013 consolidated channel (extended by TICKET-018).
   * Carries the `ShowStateV2Delta` discriminated union:
   * ShowStateDelta | LayoutStateDelta | AgendaStateDelta
   * | SimulcastInstanceStateDelta | ShowUpdateTriggerDelta
   * | ShowOfferPublishedDelta | ShowMediaPublishedDelta.
   * FE subscribes once and branches on `delta.type`.
   */
  showStateV2: (showId: string) => `fanfest:${showId}:show-state-v2`,
  /**
   * Dedicated presentation-plan channel. Carries `StageSlotStateDelta`
   * only. Subscribe-only for browser tokens — server-publish authority only.
   * Plan deltas MUST NOT be folded onto `showStateV2` or legacy contest
   * channels. Mirrors `CHANNELS.stagePlan` in realtime-channels.ts.
   */
  stagePlan: (showId: string) => `fanfest:${showId}:stage-plan`,
  stagePresence: (showId: string) => `fanfest:${showId}:stage-presence`,
  channelState: (channelId: string) => `fanfest:${channelId}:channel-state`,
  /** Zombie — legacy per-event channel. Kept as dead code; cleanup PR removes after ≥1 stable show on reactions-v2. */
  showReactions: (showId: string) =>
    `fanfest-batch:${showId}:show-reactions-events`,
  showReactionsV2: (showId: string) => `fanfest:${showId}:reactions-v2`,
  /** Per-user event channel for loyalty notifications. Mirrors CHANNELS.userEvents in realtime-channels.ts. */
  userEvents: (userId: string) => `api-events:user:${userId}`,
} as const;

// ---------------------------------------------------------------------------
// Runtime type guards
// ---------------------------------------------------------------------------

const SHOW_STATUSES = [
  "draft",
  "scheduled",
  "live",
  "ended",
  "deleted",
  "video_cast",
] as const;
const SIMULCAST_STATUSES = [
  "pending",
  "booted_up",
  "live",
  "ended",
  "failed",
] as const;
const CONTEST_STATUSES = ["draft", "ready", "ended", "deleted"] as const;
const QUIZ_TRANSITIONS = [
  "TO_QUESTION",
  "TO_CORRECT_ANSWER",
  "TO_POLL_ANSWER",
  "TO_LEADERBOARD",
  "TO_WINNER",
] as const;
const RAFFLE_RUNTIME_STATUSES = [
  "idle",
  "ready",
  "running",
  "ended",
  "cleared",
] as const;
const RAFFLE_STEPS = ["RAFFLE", "WINNER", "REWARD", "REWARD_GENERIC"] as const;
const RAFFLE_SLOT_STATES = ["IDLE", "ACTIVE", "DONE"] as const;
const LAYOUT_CONTENT_POSITIONS = ["FIRST", "LAST"] as const;
const STAGE_COMMAND_STATUSES = [
  "pending",
  "committed",
  "failed",
  "timed-out",
] as const;
const STAGE_COMMAND_REPAIR_STATUSES = [
  "none",
  "needed",
  "in_progress",
  "repaired",
  "failed",
] as const;

function isObjectRecord(x: unknown): x is Record<string, unknown> {
  return !!x && typeof x === "object";
}

function hasOwn(o: Record<string, unknown>, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(o, key);
}

function isOneOf<T extends string>(
  values: readonly T[],
  value: unknown,
): value is T {
  return (
    typeof value === "string" && (values as readonly string[]).includes(value)
  );
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function isString(value: unknown): value is string {
  return typeof value === "string";
}

function isNullableNonEmptyString(value: unknown): value is string | null {
  return value === null || isNonEmptyString(value);
}

function isEpochNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
}

function isNullableEpochNumber(value: unknown): value is number | null {
  return value === null || isEpochNumber(value);
}

function isShowStatus(value: unknown): value is ShowStatus {
  return isOneOf(SHOW_STATUSES, value);
}

function isSimulcastStatus(
  value: unknown,
): value is ShowSimulcastInstanceStatus {
  return isOneOf(SIMULCAST_STATUSES, value);
}

function isContestStatus(value: unknown): value is ContestStatus {
  return isOneOf(CONTEST_STATUSES, value);
}

function isQuizTransition(value: unknown): value is QuizTransition {
  return isOneOf(QUIZ_TRANSITIONS, value);
}

export function isRaffleRuntimeStatus(
  value: unknown,
): value is RaffleRuntimeStatus {
  return isOneOf(RAFFLE_RUNTIME_STATUSES, value);
}

function isRaffleStep(value: unknown): value is RaffleStep {
  return value === null || isOneOf(RAFFLE_STEPS, value);
}

function isRaffleSlotState(value: unknown): value is RaffleSlotState {
  return isOneOf(RAFFLE_SLOT_STATES, value);
}

function isLayoutContentPosition(
  value: unknown,
): value is LayoutContentPosition {
  return isOneOf(LAYOUT_CONTENT_POSITIONS, value);
}

function isStageCommandStatus(value: unknown): value is StageCommandStatus {
  return isOneOf(STAGE_COMMAND_STATUSES, value);
}

function isStageCommandRepairStatus(
  value: unknown,
): value is StageCommandRepairStatus {
  return isOneOf(STAGE_COMMAND_REPAIR_STATUSES, value);
}

export function isRealtimeStateDelta(x: unknown): x is RealtimeStateDelta {
  if (!x || typeof x !== "object") return false;
  const o = x as unknown as Record<string, unknown>;
  return (
    isNonEmptyString(o["type"]) &&
    isNonEmptyString(o["streamId"]) &&
    isNonEmptyString(o["entityId"]) &&
    isEpochNumber(o["epoch"]) &&
    isNonEmptyString(o["commitId"]) &&
    hasOwn(o, "commandId") &&
    isNullableNonEmptyString(o["commandId"])
  );
}

function isEnvelope(x: unknown): x is RealtimeStateDelta {
  return isRealtimeStateDelta(x);
}

export function isShowOfferPayload(x: unknown): x is ShowOfferPayload {
  if (!isObjectRecord(x)) return false;
  return (
    isString(x["id"]) &&
    isString(x["url"]) &&
    isString(x["image_url"]) &&
    isString(x["description"])
  );
}

export function isPrimaryStageSurface(x: unknown): x is PrimaryStageSurface {
  if (!isObjectRecord(x)) return false;

  if (x["kind"] === "empty") return true;

  if (x["kind"] === "quiz") {
    return (
      isNonEmptyString(x["quizId"]) &&
      hasOwn(x, "questionId") &&
      isNullableNonEmptyString(x["questionId"])
    );
  }

  if (x["kind"] === "raffle") {
    return isNonEmptyString(x["raffleId"]) && isRaffleRuntimeStatus(x["phase"]);
  }

  if (x["kind"] === "media") return isNonEmptyString(x["mediaUrl"]);

  if (x["kind"] === "offer") return isShowOfferPayload(x["offer"]);

  return false;
}

export function isPrimaryStageStateDelta(
  x: unknown,
): x is PrimaryStageStateDelta {
  if (!isEnvelope(x)) return false;
  const o = x as unknown as Record<string, unknown>;
  return (
    o["type"] === "primary-stage-state" &&
    isNonEmptyString(o["showId"]) &&
    isPrimaryStageSurface(o["surface"])
  );
}

export function isRaffleWinnerEntry(x: unknown): x is RaffleWinnerEntry {
  if (!isObjectRecord(x)) return false;
  if (!isNonEmptyString(x["userId"])) return false;
  if (!isString(x["username"])) return false;
  if (x["avatar"] !== null && !isString(x["avatar"])) return false;
  if (
    typeof x["scorePosition"] !== "number" ||
    !Number.isFinite(x["scorePosition"])
  ) {
    return false;
  }
  // Validate channelMembershipPermissionMasks is an array AND every element is a number.
  // Without the element-type check, ["admin", "superuser"] would pass and the FE's
  // permission_mask > 0 check would coerce the string truthy, breaking isPaidUser.
  if (!Array.isArray(x["channelMembershipPermissionMasks"])) return false;
  return (x["channelMembershipPermissionMasks"] as unknown[]).every(
    (m) => typeof m === "number" && Number.isFinite(m),
  );
}

export function isRaffleRuntimeState(x: unknown): x is RaffleRuntimeState {
  if (!isObjectRecord(x)) return false;
  return (
    hasOwn(x, "raffleId") &&
    isNullableNonEmptyString(x["raffleId"]) &&
    isRaffleRuntimeStatus(x["status"]) &&
    hasOwn(x, "step") &&
    isRaffleStep(x["step"]) &&
    hasOwn(x, "slotState") &&
    (x["slotState"] === null || isRaffleSlotState(x["slotState"])) &&
    Array.isArray(x["winnerUsers"]) &&
    (x["winnerUsers"] as unknown[]).every(isRaffleWinnerEntry) &&
    hasOwn(x, "phaseStartedAtEpochMs") &&
    isNullableEpochNumber(x["phaseStartedAtEpochMs"]) &&
    hasOwn(x, "phaseDurationMs") &&
    isNullableEpochNumber(x["phaseDurationMs"])
  );
}

/**
 * AC11: strict guard — rejects payloads with missing or extra-typed fields.
 * Validates envelope + discriminant + at least one entity-specific field.
 */
export function isShowStateDelta(x: unknown): x is ShowStateDelta {
  if (!isEnvelope(x)) return false;
  const o = x as unknown as Record<string, unknown>;
  if (o["type"] === "state") return isShowStatus(o["status"]);
  if (o["type"] === "recording-ready")
    return (
      (o["variant"] === "landscape" || o["variant"] === "portrait") &&
      isEpochNumber(o["completedAt"])
    );
  if (o["type"] === "mux-rotated") return true;
  return false;
}

export function isStagePresenceDelta(x: unknown): x is StagePresenceDelta {
  if (!isEnvelope(x)) return false;
  const o = x as unknown as Record<string, unknown>;
  return (
    o["type"] === "stage-presence" &&
    hasOwn(o, "currentSpeakerUserId") &&
    isNullableNonEmptyString(o["currentSpeakerUserId"]) &&
    typeof o["canFansJoinStage"] === "boolean" &&
    hasOwn(o, "hasSetupSimulfestPreparation") &&
    isNullableEpochNumber(o["hasSetupSimulfestPreparation"])
  );
}

export function isSimulcastInstanceStateDelta(
  x: unknown,
): x is SimulcastInstanceStateDelta {
  if (!isEnvelope(x)) return false;
  const o = x as unknown as Record<string, unknown>;
  return (
    o["type"] === "simulcast-state" &&
    isNonEmptyString(o["instanceId"]) &&
    isSimulcastStatus(o["status"]) &&
    (o["variant"] === "landscape" || o["variant"] === "portrait")
  );
}

export function isQuizStateDelta(x: unknown): x is QuizStateDelta {
  if (!isEnvelope(x)) return false;
  const o = x as unknown as Record<string, unknown>;
  return (
    o["type"] === "quiz-state" &&
    isNonEmptyString(o["quizId"]) &&
    hasOwn(o, "currentQuestionId") &&
    isNullableNonEmptyString(o["currentQuestionId"]) &&
    isContestStatus(o["quizStatus"])
  );
}

export function isQuizTransitionDelta(x: unknown): x is QuizTransitionDelta {
  if (!isEnvelope(x)) return false;
  const o = x as unknown as Record<string, unknown>;
  return (
    o["type"] === "quiz-transition" &&
    isNonEmptyString(o["quizId"]) &&
    isQuizTransition(o["transition"])
  );
}

export function isQuizWinnerEntry(x: unknown): x is QuizWinnerEntry {
  if (!isObjectRecord(x)) return false;
  if (!isNonEmptyString(x["userId"])) return false;
  if (!isString(x["username"])) return false;
  if (x["avatar"] !== null && !isString(x["avatar"])) return false;
  return true;
}

export function isQuizWinnerDelta(x: unknown): x is QuizWinnerDelta {
  if (!isEnvelope(x)) return false;
  const o = x as unknown as Record<string, unknown>;
  if (o["type"] !== "quiz-winner") return false;
  if (!isNonEmptyString(o["quizId"])) return false;
  if (!Array.isArray(o["winnerUsers"])) return false;
  return (o["winnerUsers"] as unknown[]).every(isQuizWinnerEntry);
}

export function isRaffleStateDelta(x: unknown): x is RaffleStateDelta {
  if (!isEnvelope(x)) return false;
  const o = x as unknown as Record<string, unknown>;
  return (
    o["type"] === "raffle-state" &&
    isNonEmptyString(o["raffleId"]) &&
    isContestStatus(o["raffleStatus"]) &&
    isEpochNumber(o["entryCount"])
  );
}

export function isRaffleWinnerDelta(x: unknown): x is RaffleWinnerDelta {
  if (!isEnvelope(x)) return false;
  const o = x as unknown as Record<string, unknown>;
  if (o["type"] !== "raffle-winner") return false;
  if (!isNonEmptyString(o["raffleId"])) return false;
  if (!Array.isArray(o["winnerUsers"])) return false;
  return (o["winnerUsers"] as unknown[]).every(isRaffleWinnerEntry);
}

export function isAgendaStateDelta(x: unknown): x is AgendaStateDelta {
  if (!isEnvelope(x)) return false;
  const o = x as unknown as Record<string, unknown>;
  return (
    o["type"] === "agenda-state" &&
    hasOwn(o, "currentAgendaItemId") &&
    isNullableNonEmptyString(o["currentAgendaItemId"]) &&
    typeof o["isFullScreen"] === "boolean"
  );
}

export function isQuizListChangedDelta(x: unknown): x is QuizListChangedDelta {
  if (!isEnvelope(x)) return false;
  const o = x as unknown as Record<string, unknown>;
  return (
    o["type"] === "quiz-list-changed" &&
    isNonEmptyString(o["channelId"]) &&
    (o["changeType"] === "created" ||
      o["changeType"] === "deleted" ||
      o["changeType"] === "edited" ||
      o["changeType"] === "activated" ||
      o["changeType"] === "deactivated") &&
    isNonEmptyString(o["quizId"])
  );
}

/**
 * Combined guard for the always-on `fanfest:{channelId}:quiz-list` channel.
 * OR-combines the two delta types that channel now carries
 * (`quiz-list-changed` + `quiz-winner`). A miss here silently drops the delta
 * inside `usePushStateChannel.applyDelta`; every consumer MUST switch on
 * `delta.type` afterwards (Pattern B).
 */
export function isQuizListDelta(
  x: unknown,
): x is QuizListChangedDelta | QuizWinnerDelta {
  return isQuizListChangedDelta(x) || isQuizWinnerDelta(x);
}

export function isRaffleListChangedDelta(
  x: unknown,
): x is RaffleListChangedDelta {
  if (!isEnvelope(x)) return false;
  const o = x as unknown as Record<string, unknown>;
  return (
    o["type"] === "raffle-list-changed" &&
    isNonEmptyString(o["channelId"]) &&
    (o["changeType"] === "created" ||
      o["changeType"] === "deleted" ||
      o["changeType"] === "edited" ||
      o["changeType"] === "activated" ||
      o["changeType"] === "deactivated") &&
    isNonEmptyString(o["raffleId"])
  );
}

/**
 * Combined guard for the always-on `fanfest:{channelId}:raffle-list` channel.
 * OR-combines the two delta types that channel now carries
 * (`raffle-list-changed` + `raffle-winner`). A miss here silently drops the
 * delta inside `usePushStateChannel.applyDelta`; every consumer MUST switch on
 * `delta.type` afterwards (Pattern B).
 */
export function isRaffleListDelta(
  x: unknown,
): x is RaffleListChangedDelta | RaffleWinnerDelta {
  return isRaffleListChangedDelta(x) || isRaffleWinnerDelta(x);
}

export function isRaffleStepDelta(x: unknown): x is RaffleStepDelta {
  if (!isEnvelope(x)) return false;
  const o = x as unknown as Record<string, unknown>;
  return (
    o["type"] === "raffle-step" &&
    isNonEmptyString(o["raffleId"]) &&
    hasOwn(o, "step") &&
    isRaffleStep(o["step"]) &&
    hasOwn(o, "phaseStartedAtEpochMs") &&
    isNullableEpochNumber(o["phaseStartedAtEpochMs"]) &&
    hasOwn(o, "phaseDurationMs") &&
    isNullableEpochNumber(o["phaseDurationMs"])
  );
}

export function isRaffleSlotDelta(x: unknown): x is RaffleSlotDelta {
  if (!isEnvelope(x)) return false;
  const o = x as unknown as Record<string, unknown>;
  return (
    o["type"] === "raffle-slot" &&
    isNonEmptyString(o["raffleId"]) &&
    isRaffleSlotState(o["slotState"])
  );
}

/**
 * Combined guard for the `fanfest:{showId}:raffle` channel — TICKET-019.
 * OR-combines all four raffle delta type guards. A miss here silently drops
 * the delta inside `usePushStateChannel.applyDelta`.
 */
export function isRaffleDelta(
  x: unknown,
): x is
  | RaffleStateDelta
  | RaffleWinnerDelta
  | RaffleStepDelta
  | RaffleSlotDelta {
  return (
    isRaffleStateDelta(x) ||
    isRaffleWinnerDelta(x) ||
    isRaffleStepDelta(x) ||
    isRaffleSlotDelta(x)
  );
}

export function isLayoutStateDelta(x: unknown): x is LayoutStateDelta {
  if (!isEnvelope(x)) return false;
  const o = x as unknown as Record<string, unknown>;
  return (
    o["type"] === "layout-state" &&
    isLayoutContentPosition(o["contentPosition"])
  );
}

export function isShowUpdateTriggerDelta(
  x: unknown,
): x is ShowUpdateTriggerDelta {
  if (!isEnvelope(x)) return false;
  const o = x as unknown as Record<string, unknown>;
  return (
    o["type"] === "update-trigger" && isEpochNumber(o["lastUpdateTimeEpoch"])
  );
}

export function isShowOfferPublishedDelta(
  x: unknown,
): x is ShowOfferPublishedDelta {
  if (!isEnvelope(x)) return false;
  const o = x as unknown as Record<string, unknown>;
  if (o["type"] !== "offer-state") return false;
  if (!hasOwn(o, "offer")) return false;
  // offer is either null or an object with required string fields
  if (o["offer"] === null) return true;
  return isShowOfferPayload(o["offer"]);
}

export function isShowMediaPublishedDelta(
  x: unknown,
): x is ShowMediaPublishedDelta {
  if (!isEnvelope(x)) return false;
  const o = x as unknown as Record<string, unknown>;
  return (
    o["type"] === "media-state" &&
    hasOwn(o, "mediaUrl") &&
    (o["mediaUrl"] === null || isString(o["mediaUrl"]))
  );
}

/**
 * Combined guard for the consolidated `show-state-v2` channel — TICKET-013 + TICKET-018.
 * OR-combines all sub-type guards. A miss here silently drops the delta
 * inside `usePushStateChannel.applyDelta`, so any new sub-type added to
 * `ShowStateV2Delta` MUST add a clause to this guard.
 */
export function isShowStateV2Delta(x: unknown): x is ShowStateV2Delta {
  return (
    isPrimaryStageStateDelta(x) ||
    isShowStateDelta(x) ||
    isLayoutStateDelta(x) ||
    isAgendaStateDelta(x) ||
    isSimulcastInstanceStateDelta(x) ||
    isShowUpdateTriggerDelta(x) ||
    isShowOfferPublishedDelta(x) ||
    isShowMediaPublishedDelta(x)
  );
}

// ---------------------------------------------------------------------------
// Stage presentation plan guards
// ---------------------------------------------------------------------------

export function isStageSlotId(value: unknown): value is StageSlotId {
  return isOneOf(STAGE_SLOT_IDS, value);
}

function isStageContentKind(value: unknown): value is StageContentKind {
  return (
    value === "quiz" ||
    value === "raffle" ||
    value === "media" ||
    value === "offer" ||
    value === "agenda" ||
    value === "layout"
  );
}

export function isStagePresentationPhaseName(
  value: unknown,
): value is StagePresentationPhaseName {
  return isOneOf(STAGE_PRESENTATION_PHASE_NAMES, value);
}

export function isStageContentTarget(x: unknown): x is StageContentTarget {
  if (!isObjectRecord(x)) return false;

  if (x["kind"] === "quiz") {
    return (
      isNonEmptyString(x["quizId"]) &&
      hasOwn(x, "questionId") &&
      isNullableNonEmptyString(x["questionId"])
    );
  }

  if (x["kind"] === "raffle") return isNonEmptyString(x["raffleId"]);

  if (x["kind"] === "media") return isNonEmptyString(x["mediaUrl"]);

  if (x["kind"] === "offer") return isShowOfferPayload(x["offer"]);

  if (x["kind"] === "agenda") {
    return (
      hasOwn(x, "currentAgendaItemId") &&
      isNullableNonEmptyString(x["currentAgendaItemId"]) &&
      typeof x["isFullScreen"] === "boolean"
    );
  }

  if (x["kind"] === "layout") {
    return isLayoutContentPosition(x["contentPosition"]);
  }

  return false;
}

export function isStageSlotRegistryEntry(
  x: unknown,
): x is StageSlotRegistryEntry {
  if (!isObjectRecord(x)) return false;
  return (
    isStageSlotId(x["slotId"]) &&
    typeof x["exclusive"] === "boolean" &&
    Array.isArray(x["allowedContentKinds"]) &&
    (x["allowedContentKinds"] as unknown[]).every(isStageContentKind) &&
    isString(x["description"])
  );
}

function isStagePresentationPhasePayload(
  x: unknown,
): x is StagePresentationPhasePayload {
  if (!isObjectRecord(x)) return false;
  if (x["kind"] === "static") return true;
  if (x["kind"] === "quiz") {
    return (
      isNonEmptyString(x["quizId"]) &&
      hasOwn(x, "questionId") &&
      isNullableNonEmptyString(x["questionId"])
    );
  }
  if (x["kind"] === "raffle") return isNonEmptyString(x["raffleId"]);
  return false;
}

export function isStagePresentationPhase(
  x: unknown,
): x is StagePresentationPhase {
  if (!isObjectRecord(x)) return false;
  if (!isStagePresentationPhaseName(x["phase"])) return false;
  // startsAtMs is an offset from the plan start — a non-negative safe integer.
  if (!isEpochNumber(x["startsAtMs"])) return false;
  // durationMs is a non-negative safe integer, or null for terminal phases.
  if (!isNullableEpochNumber(x["durationMs"])) return false;
  if (!hasOwn(x, "payload")) return false;
  if (x["payload"] !== null && !isStagePresentationPhasePayload(x["payload"])) {
    return false;
  }
  if (hasOwn(x, "engagementOpen") && typeof x["engagementOpen"] !== "boolean") {
    return false;
  }
  return true;
}

const STAGE_PRESENTATION_PLAN_STATUSES = [
  "active",
  "transitioning",
  "ended",
  "empty",
] as const;

function isStagePresentationPlanStatus(
  value: unknown,
): value is StagePresentationPlanStatus {
  return isOneOf(STAGE_PRESENTATION_PLAN_STATUSES, value);
}

export function isStagePresentationPlan(
  x: unknown,
): x is StagePresentationPlan {
  if (!isObjectRecord(x)) return false;
  if (!isStageSlotId(x["slotId"])) return false;
  if (!isNonEmptyString(x["planId"])) return false;
  // planVersion is a positive safe integer; the contract starts it at 1.
  if (
    typeof x["planVersion"] !== "number" ||
    !Number.isSafeInteger(x["planVersion"]) ||
    x["planVersion"] < 1
  ) {
    return false;
  }
  if (!isStageContentTarget(x["content"])) return false;
  if (!isStagePresentationPlanStatus(x["status"])) return false;
  if (!isNonEmptyString(x["committedByHostId"])) return false;
  // Epoch fields are epoch milliseconds — ISO strings must be rejected.
  if (!isEpochNumber(x["serverCommittedAtEpochMs"])) return false;
  if (!isEpochNumber(x["serverStartedAtEpochMs"])) return false;
  if (
    !hasOwn(x, "validUntilEpochMs") ||
    !isNullableEpochNumber(x["validUntilEpochMs"])
  ) {
    return false;
  }
  if (
    hasOwn(x, "engagementClosesAtEpochMs") &&
    !isNullableEpochNumber(x["engagementClosesAtEpochMs"])
  ) {
    return false;
  }
  if (!Array.isArray(x["phases"])) return false;
  return (x["phases"] as unknown[]).every(isStagePresentationPhase);
}

const STAGE_SLOT_LOCK_STATES = ["pending", "occupied", "empty"] as const;

function isStageSlotLockState(value: unknown): value is StageSlotLockState {
  return isOneOf(STAGE_SLOT_LOCK_STATES, value);
}

export function isStageSlotLock(x: unknown): x is StageSlotLock {
  if (!isObjectRecord(x)) return false;
  return (
    isStageSlotId(x["slotId"]) &&
    isStageSlotLockState(x["lockState"]) &&
    hasOwn(x, "planId") &&
    isNullableNonEmptyString(x["planId"]) &&
    hasOwn(x, "commandId") &&
    isNullableNonEmptyString(x["commandId"]) &&
    hasOwn(x, "committedByHostId") &&
    isNullableNonEmptyString(x["committedByHostId"]) &&
    hasOwn(x, "lockedUntilEpochMs") &&
    isNullableEpochNumber(x["lockedUntilEpochMs"])
  );
}

export function isStageSlotStateDelta(x: unknown): x is StageSlotStateDelta {
  if (!isEnvelope(x)) return false;
  const o = x as unknown as Record<string, unknown>;
  if (o["type"] !== "stage-slot-state") return false;
  if (!isNonEmptyString(o["showId"])) return false;
  if (!isStageSlotId(o["slotId"])) return false;
  if (!isNonEmptyString(o["committedByHostId"])) return false;
  if (!hasOwn(o, "validUntilEpochMs")) return false;
  if (!isNullableEpochNumber(o["validUntilEpochMs"])) return false;
  if (!hasOwn(o, "plan")) return false;
  if (o["plan"] !== null && !isStagePresentationPlan(o["plan"])) return false;
  return true;
}

// ---------------------------------------------------------------------------
// Pure phase / effective-slot projection
// ---------------------------------------------------------------------------
//
// Imported by the backend (command conflict checks) and the frontend
// (rendering). Both ends call the SAME functions so a slot is empty at the
// same server time everywhere — this is the multi-host unlock mechanism.

/**
 * Find the phase active at `offsetMs` (ms since `serverStartedAtEpochMs`).
 *
 * A phase covers `[startsAtMs, startsAtMs + durationMs)`. A terminal phase
 * (`durationMs === null`) covers `[startsAtMs, ∞)`. Returns `null` when no
 * phase covers the offset (offset before the first phase, or past the end of
 * a fully timed plan). Phases are assumed ordered by `startsAtMs`.
 */
export function phaseAt(
  offsetMs: number,
  phases: ReadonlyArray<StagePresentationPhase>,
): { phase: StagePresentationPhase; index: number } | null {
  for (let index = phases.length - 1; index >= 0; index -= 1) {
    const phase = phases[index];
    if (phase === undefined) continue;
    if (offsetMs < phase.startsAtMs) continue;
    if (phase.durationMs === null) return { phase, index };
    if (offsetMs < phase.startsAtMs + phase.durationMs) return { phase, index };
    // offset is past this timed phase; only a later terminal phase could
    // still match, but later phases start later — so nothing matches.
    return null;
  }
  return null;
}

/**
 * Project a presentation plan to its {@link EffectiveSlotState} at
 * `serverNowEpochMs`.
 *
 * Rules (effective surface):
 * 1. No plan -> empty.
 * 2. `serverNowEpochMs < serverStartedAtEpochMs` -> scheduled (not live).
 * 3. `validUntilEpochMs !== null` and `serverNowEpochMs >= validUntilEpochMs`
 *    -> empty (expired).
 * 4. A phase active at the current offset -> active, rendering that phase.
 * 5. No phase covers the current offset -> empty.
 * 6. Engagement is open only when the active phase is an engagement phase AND
 *    `engagementClosesAtEpochMs` has not passed.
 *
 * Supersession (rule 6 in the design) is handled by the caller picking the
 * higher-epoch plan before calling this function — it is an epoch ordering
 * concern, not a projection concern.
 */
export function effectiveSlot(
  plan: StagePresentationPlan | null,
  serverNowEpochMs: number,
): EffectiveSlotState {
  if (plan === null) return { kind: "empty" };

  if (serverNowEpochMs < plan.serverStartedAtEpochMs) {
    return { kind: "scheduled", plan };
  }

  if (
    plan.validUntilEpochMs !== null &&
    serverNowEpochMs >= plan.validUntilEpochMs
  ) {
    return { kind: "empty" };
  }

  const phaseOffsetMs = serverNowEpochMs - plan.serverStartedAtEpochMs;
  const active = phaseAt(phaseOffsetMs, plan.phases);
  if (active === null) return { kind: "empty" };

  const engagementClosesAtEpochMs = plan.engagementClosesAtEpochMs ?? null;
  const engagementOpen =
    active.phase.engagementOpen === true &&
    (engagementClosesAtEpochMs === null ||
      serverNowEpochMs < engagementClosesAtEpochMs);

  return {
    kind: "active",
    plan,
    phase: active.phase,
    phaseIndex: active.index,
    phaseOffsetMs,
    engagementOpen,
  };
}

/**
 * Alias of {@link effectiveSlot} matching the name used by the backend
 * effective-slot evaluator and the frontend projector. Kept as a single shared
 * implementation so the two ends can never diverge.
 */
export const getEffectiveSlotState = effectiveSlot;

export function isEffectiveSlotEmpty(state: EffectiveSlotState): boolean {
  return state.kind === "empty";
}

/**
 * Effective runtime for an entire show: every stage slot projected to its
 * {@link EffectiveSlotState} at one `serverNowEpochMs`. Backend command
 * conflict checks and the frontend projector both read this so a slot is empty
 * at the same server time everywhere.
 */
export type EffectiveShowRuntime = Readonly<
  Record<StageSlotId, EffectiveSlotState>
>;

/**
 * Project every slot in a {@link ShowRuntimeSnapshot} to its effective state at
 * `serverNowEpochMs`. The snapshot's stored `slot.effective` is the projection
 * at the snapshot's own `serverNowEpochMs`; this re-projects from the raw
 * `slot.plan` so a caller can evaluate the runtime at any later server time
 * (the slot keys never carry a denormalized surface — approach B: plan + clock
 * projection, not snapshot-embedded effective).
 */
export function getEffectiveShowRuntime(
  snapshot: ShowRuntimeSnapshot,
  serverNowEpochMs: number,
): EffectiveShowRuntime {
  const runtime = {} as Record<StageSlotId, EffectiveSlotState>;
  for (const slotId of STAGE_SLOT_IDS) {
    runtime[slotId] = effectiveSlot(
      snapshot.slots[slotId].plan,
      serverNowEpochMs,
    );
  }
  return runtime;
}

/**
 * Result of {@link assertSlotAvailable}. A pure value, never a thrown error —
 * the shared projection layer is framework-free, so the backend maps an
 * `available: false` result onto its `StageCommandRejected` union and the
 * frontend uses it for advisory control state.
 */
export type SlotAvailability =
  | { readonly available: true }
  | {
      /**
       * The slot is held by a different effective plan. `occupiedBy` carries
       * the content already on the slot so a caller can name the conflict.
       */
      readonly available: false;
      readonly reason: "slot-occupied";
      readonly occupiedBy: StageContentTarget;
    };

/**
 * Returns whether `requestedContent` may take `slotId` at `serverNowEpochMs`.
 *
 * A slot is available when:
 * 1. it is non-exclusive (`agenda`, `layout` — auxiliary streams never block),
 * 2. its effective state is empty (no plan, expired, or past the last phase),
 *    or
 * 3. the requested content is the SAME content already on the slot — an
 *    idempotent re-activation or a lifecycle advance of the running content
 *    (e.g. the next quiz question), which is not a conflict.
 *
 * Cross-content activation onto an occupied exclusive slot is reported
 * `available: false`; the backend treats that as authorized supersession,
 * NOT a hard rejection — only the commit-race / pending-command surface emits
 * `STAGE_SLOT_OCCUPIED`.
 */
export function assertSlotAvailable(
  snapshot: ShowRuntimeSnapshot,
  slotId: StageSlotId,
  requestedContent: StageContentTarget,
  serverNowEpochMs: number,
): SlotAvailability {
  if (!STAGE_SLOT_REGISTRY[slotId].exclusive) return { available: true };

  const effective = effectiveSlot(
    snapshot.slots[slotId].plan,
    serverNowEpochMs,
  );
  if (effective.kind === "empty") return { available: true };

  const occupiedBy = effective.plan.content;
  if (isSameStageContentTarget(occupiedBy, requestedContent)) {
    return { available: true };
  }
  return { available: false, reason: "slot-occupied", occupiedBy };
}

/**
 * Whether two content targets address the same underlying content entity.
 * Two quiz targets match on `quizId` (a different `questionId` is a lifecycle
 * advance of the same quiz, not a different content); raffles match on
 * `raffleId`; media on `mediaUrl`; offers on `offer.id`; agenda/layout match by
 * kind (single auxiliary stream each).
 */
function isSameStageContentTarget(
  a: StageContentTarget,
  b: StageContentTarget,
): boolean {
  if (a.kind !== b.kind) return false;
  switch (a.kind) {
    case "quiz":
      return a.quizId === (b as { quizId: string }).quizId;
    case "raffle":
      return a.raffleId === (b as { raffleId: string }).raffleId;
    case "media":
      return a.mediaUrl === (b as { mediaUrl: string }).mediaUrl;
    case "offer":
      return a.offer.id === (b as { offer: ShowOfferPayload }).offer.id;
    case "agenda":
    case "layout":
      return true;
  }
}

/**
 * Derive host-visible {@link StageSlotLock} metadata for every stage slot from
 * pending {@link StageCommandOperation} records plus committed slot plans
 * evaluated at `serverNowEpochMs`. There is NO separate occupancy mutex — lock
 * state is purely a function of these two inputs (multi-host locking).
 *
 * Per slot, lock state is:
 * - `pending`  — a non-timed-out pending command writes the slot's stream,
 * - `occupied` — no pending command, but the committed plan is effectively
 *   active or scheduled,
 * - `empty`    — neither.
 *
 * A pending command takes precedence over a committed plan: a host must see a
 * slot as locked while a command is mid-flight even before its plan commits.
 */
export function slotLocksFromSnapshot(
  snapshot: ShowRuntimeSnapshot,
  hostOperations: ReadonlyArray<StageCommandOperation>,
  serverNowEpochMs: number,
): StageSlotLock[] {
  const pendingByStream = new Set<string>();
  let pendingForShow: StageCommandOperation | null = null;
  for (const operation of hostOperations) {
    if (operation.status !== "pending") continue;
    if (operation.timeoutAtEpochMs <= serverNowEpochMs) continue;
    for (const streamId of operation.intendedStreams) {
      pendingByStream.add(streamId);
    }
    if (pendingForShow === null) pendingForShow = operation;
  }

  return STAGE_SLOT_IDS.map((slotId): StageSlotLock => {
    const slotStreamId = STREAMS.stagePlan(snapshot.showId, slotId);
    const pendingWritesSlot = pendingByStream.has(slotStreamId);

    if (pendingWritesSlot && pendingForShow !== null) {
      return {
        slotId,
        lockState: "pending",
        planId: null,
        commandId: pendingForShow.commandId,
        committedByHostId: pendingForShow.hostUserId,
        lockedUntilEpochMs: pendingForShow.timeoutAtEpochMs,
      };
    }

    const plan = snapshot.slots[slotId].plan;
    const effective = effectiveSlot(plan, serverNowEpochMs);
    if (effective.kind === "empty" || plan === null) {
      return {
        slotId,
        lockState: "empty",
        planId: null,
        commandId: null,
        committedByHostId: null,
        lockedUntilEpochMs: null,
      };
    }

    return {
      slotId,
      lockState: "occupied",
      planId: plan.planId,
      commandId: plan.planId,
      committedByHostId: plan.committedByHostId,
      lockedUntilEpochMs: plan.validUntilEpochMs,
    };
  });
}

export function isStageSlotSnapshot(x: unknown): x is StageSlotSnapshot {
  if (!isObjectRecord(x)) return false;
  if (!isStageSlotId(x["slotId"])) return false;
  if (!hasOwn(x, "plan")) return false;
  if (x["plan"] !== null && !isStagePresentationPlan(x["plan"])) return false;
  if (!isStageSlotLock(x["lock"])) return false;
  // `effective` is a derived projection; a structural shape check is enough.
  const effective = x["effective"];
  if (!isObjectRecord(effective)) return false;
  return (
    effective["kind"] === "empty" ||
    effective["kind"] === "scheduled" ||
    effective["kind"] === "active"
  );
}

export function isStageCommandTarget(x: unknown): x is StageCommandTarget {
  if (!isObjectRecord(x)) return false;

  if (x["kind"] === "quiz") {
    if (!isNonEmptyString(x["quizId"])) return false;
    if (x["action"] === "activate" || x["action"] === "end") return true;
    if (
      x["action"] === "activate-question" ||
      x["action"] === "deactivate-question" ||
      x["action"] === "end-question"
    ) {
      return isNonEmptyString(x["questionId"]);
    }
    if (x["action"] === "transition") return isQuizTransition(x["transition"]);
    return false;
  }

  if (x["kind"] === "raffle") {
    if (!isNonEmptyString(x["raffleId"])) return false;
    if (
      x["action"] === "activate" ||
      x["action"] === "start" ||
      x["action"] === "end" ||
      x["action"] === "deactivate"
    ) {
      return true;
    }
    if (x["action"] === "slot") return isRaffleSlotState(x["slotState"]);
    if (x["action"] === "step") {
      return hasOwn(x, "step") && isRaffleStep(x["step"]);
    }
    return false;
  }

  if (x["kind"] === "media") {
    return hasOwn(x, "mediaUrl") && isNullableNonEmptyString(x["mediaUrl"]);
  }

  if (x["kind"] === "offer") {
    return (
      hasOwn(x, "offer") &&
      (x["offer"] === null || isShowOfferPayload(x["offer"]))
    );
  }

  if (x["kind"] === "layout") {
    return isLayoutContentPosition(x["contentPosition"]);
  }

  if (x["kind"] === "agenda") {
    return (
      hasOwn(x, "currentAgendaItemId") &&
      isNullableNonEmptyString(x["currentAgendaItemId"]) &&
      typeof x["isFullScreen"] === "boolean"
    );
  }

  return x["kind"] === "show-update" || x["kind"] === "reset-stage";
}

export function isStageCommandOperation(
  x: unknown,
): x is StageCommandOperation {
  if (!isObjectRecord(x)) return false;
  return (
    isNonEmptyString(x["commandId"]) &&
    isNonEmptyString(x["showId"]) &&
    isNonEmptyString(x["hostUserId"]) &&
    isStageCommandTarget(x["target"]) &&
    isStageCommandStatus(x["status"]) &&
    isEpochNumber(x["startedAtEpochMs"]) &&
    isEpochNumber(x["updatedAtEpochMs"]) &&
    isEpochNumber(x["timeoutAtEpochMs"]) &&
    Array.isArray(x["intendedStreams"]) &&
    (x["intendedStreams"] as unknown[]).every(isNonEmptyString) &&
    Array.isArray(x["committedEnvelopes"]) &&
    (x["committedEnvelopes"] as unknown[]).every(isRealtimeStateDelta) &&
    hasOwn(x, "failureCode") &&
    isNullableNonEmptyString(x["failureCode"]) &&
    hasOwn(x, "failureMessage") &&
    isNullableNonEmptyString(x["failureMessage"]) &&
    isStageCommandRepairStatus(x["repairStatus"]) &&
    isEpochNumber(x["retryCount"])
  );
}

export function isRuntimeStreamEnvelope(
  x: unknown,
): x is RuntimeStreamEnvelope {
  if (!isObjectRecord(x)) return false;
  if (!isRealtimeStateDelta(x["delta"])) return false;
  return (
    isNonEmptyString(x["streamId"]) &&
    isEpochNumber(x["epoch"]) &&
    x["streamId"] === x["delta"].streamId &&
    x["epoch"] === x["delta"].epoch &&
    isEpochNumber(x["updatedAtEpochMs"])
  );
}

function isStageSlotRecord(
  x: unknown,
): x is Record<StageSlotId, StageSlotSnapshot> {
  if (!isObjectRecord(x)) return false;
  // Every registry slot must be present and a valid slot snapshot.
  return STAGE_SLOT_IDS.every(
    (slotId) => hasOwn(x, slotId) && isStageSlotSnapshot(x[slotId]),
  );
}

export function isShowRuntimeSnapshot(x: unknown): x is ShowRuntimeSnapshot {
  if (!isObjectRecord(x)) return false;
  if (!isNonEmptyString(x["showId"])) return false;
  // Snapshot exposes server time as epoch ms and slot-keyed state.
  if (!isEpochNumber(x["serverNowEpochMs"])) return false;
  if (!isStageSlotRecord(x["slots"])) return false;
  if (!Array.isArray(x["slotLocks"])) return false;
  if (!(x["slotLocks"] as unknown[]).every(isStageSlotLock)) return false;
  if (!Array.isArray(x["streams"])) return false;
  if (!(x["streams"] as unknown[]).every(isRuntimeStreamEnvelope)) return false;
  if (!hasOwn(x, "hostOperations")) return true;
  return (
    Array.isArray(x["hostOperations"]) &&
    (x["hostOperations"] as unknown[]).every(isStageCommandOperation)
  );
}

export function isRecentAvatarEntry(x: unknown): x is RecentAvatarEntry {
  if (!isObjectRecord(x)) return false;
  return (
    isNonEmptyString(x["userId"]) &&
    isString(x["username"]) &&
    (x["avatar"] === null || isString(x["avatar"])) &&
    isEpochNumber(x["enteredAtEpochMs"])
  );
}

export function isRecentAvatarsDelta(x: unknown): x is RecentAvatarsDelta {
  if (!isEnvelope(x)) return false;
  const o = x as unknown as Record<string, unknown>;
  return (
    o["type"] === "recent-avatars" &&
    isNonEmptyString(o["channelId"]) &&
    Array.isArray(o["recentAvatars"]) &&
    (o["recentAvatars"] as unknown[]).every(isRecentAvatarEntry)
  );
}

export function isReactionEvent(x: unknown): x is ReactionEvent {
  if (!isObjectRecord(x)) return false;
  return (
    isNonEmptyString(x["userId"]) &&
    isNonEmptyString(x["reactionType"]) &&
    isEpochNumber(x["clientTimestampMs"])
  );
}

export function isReactionsBatchDelta(x: unknown): x is ReactionsBatchDelta {
  if (!isEnvelope(x)) return false;
  const o = x as unknown as Record<string, unknown>;
  return (
    o["type"] === "reactions-batch" &&
    Array.isArray(o["reactions"]) &&
    (o["reactions"] as unknown[]).every(isReactionEvent) &&
    isEpochNumber(o["batchEpochMs"])
  );
}
